import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Copy, Download, ImageOff, Images, Layers, Loader2, Megaphone, Palette, Search, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import CarouselBuilder from '@/components/admin/ads/CarouselBuilder';
import BrandAdBuilder from '@/components/admin/ads/BrandAdBuilder';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

const SIZES = [
  { key: 'square', label: 'Square', dims: '1080×1080', hint: 'IG / FB feed' },
  { key: 'portrait', label: 'Portrait', dims: '1080×1350', hint: 'IG feed 4:5' },
  { key: 'story', label: 'Story / Reel', dims: '1080×1920', hint: 'Stories, TikTok' },
  { key: 'landscape', label: 'Landscape', dims: '1200×628', hint: 'FB link ads, X' },
];

const THEMES = [
  { key: 'light', label: 'Light', swatch: '#FFFAF5', ring: '#FED7A5' },
  { key: 'dark', label: 'Dark', swatch: '#534145', ring: '#D4A4A8' },
];

const MODES = [
  { key: 'single', label: 'Single Ad', icon: Images },
  { key: 'carousel', label: 'Carousel', icon: Layers },
  { key: 'brand', label: 'Brand Ad', icon: Megaphone },
];

const PLATFORMS = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'x', label: 'X' },
];

const TONES = [
  { key: 'heartfelt', label: 'Heartfelt' },
  { key: 'playful', label: 'Playful' },
  { key: 'minimal', label: 'Minimal' },
];

export default function AdminAds() {
  const [mode, setMode] = useState('single');
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [theme, setTheme] = useState('light');
  const [imageIndex, setImageIndex] = useState(0);
  const [previewSize, setPreviewSize] = useState('square');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [checkedSizes, setCheckedSizes] = useState(() => new Set(SIZES.map((s) => s.key)));
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [tone, setTone] = useState('heartfelt');
  const [caption, setCaption] = useState('');
  const [captionLoading, setCaptionLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const previewUrlRef = useRef(null);

  useEffect(() => {
    api.get('/products').then((res) => setProducts(res.data)).catch(() => toast.error('Failed to load products'));
  }, []);

  // Re-render preview whenever product / theme / size / photo change
  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setPreviewLoading(true);
    api.get('/admin/ads/render', {
      params: { product_id: selected.id, size: previewSize, theme, image_index: imageIndex },
      responseType: 'blob',
    })
      .then((res) => {
        if (cancelled) return;
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        const url = URL.createObjectURL(res.data);
        previewUrlRef.current = url;
        setPreviewUrl(url);
      })
      .catch(() => !cancelled && toast.error('Preview failed — does this product have an image?'))
      .finally(() => !cancelled && setPreviewLoading(false));
    return () => { cancelled = true; };
  }, [selected, theme, previewSize, imageIndex]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const list = q ? products.filter((p) => p.name.toLowerCase().includes(q)) : products;
    return list.slice(0, 50);
  }, [products, search]);

  const toggleSize = (key) => {
    setCheckedSizes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const downloadSelected = async () => {
    if (!selected || checkedSizes.size === 0) return;
    setDownloading(true);
    try {
      const sizes = SIZES.filter((s) => checkedSizes.has(s.key));
      for (let i = 0; i < sizes.length; i += 1) {
        setDownloadProgress(`Rendering ${sizes[i].label} (${i + 1}/${sizes.length})…`);
        const res = await api.get('/admin/ads/render', {
          params: { product_id: selected.id, size: sizes[i].key, theme, image_index: imageIndex },
          responseType: 'blob',
        });
        const disposition = res.headers['content-disposition'] || '';
        const match = disposition.match(/filename="(.+)"/);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res.data);
        a.download = match ? match[1] : `mementa-ad-${sizes[i].key}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      }
      toast.success(`${sizes.length} ad image${sizes.length > 1 ? 's' : ''} downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(false);
      setDownloadProgress('');
    }
  };

  const generateCaption = async () => {
    if (!selected) return;
    setCaptionLoading(true);
    try {
      const res = await api.post('/admin/ads/caption', { product_id: selected.id, platform, tone });
      setCaption(res.data.caption);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Caption generation failed');
    } finally {
      setCaptionLoading(false);
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Caption copied');
    } catch {
      toast.error('Copy failed — select the text manually');
    }
  };

  const themePickerEl = (
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#9E6752' }}>Theme</span>
      {THEMES.map((t) => (
        <button
          key={t.key}
          onClick={() => setTheme(t.key)}
          data-testid={`ads-theme-${t.key}`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
          style={{
            background: theme === t.key ? '#534145' : '#FFF8F0',
            color: theme === t.key ? '#FFFAF5' : '#534145',
            border: '1px solid #53414533',
          }}
        >
          <span className="w-3 h-3 rounded-full inline-block" style={{ background: t.swatch, border: `1.5px solid ${t.ring}` }} />
          {t.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-ads-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#20212B' }}>
          <Palette size={22} style={{ color: '#9E6752' }} /> Ad Studio
        </h1>
        <p className="text-sm mt-1" style={{ color: '#9E6752' }}>
          Create Mementa-branded ad creative — single product ads, swipeable carousels, and brand-awareness ads.
        </p>
      </div>

      {/* Mode tabs */}
      <div className="flex gap-1.5 flex-wrap" data-testid="ads-mode-tabs">
        {MODES.map((m) => {
          const Icon = m.icon;
          return (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              data-testid={`ads-mode-${m.key}`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all"
              style={{
                background: mode === m.key ? '#534145' : 'white',
                color: mode === m.key ? '#FFFAF5' : '#534145',
                border: '1px solid #53414533',
              }}
            >
              <Icon size={15} />
              {m.label}
            </button>
          );
        })}
      </div>

      {mode === 'single' && (
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Product picker */}
        <div className="lg:col-span-2 rounded-2xl bg-white border p-4" style={{ borderColor: '#E5E0DA' }}>
          <div className="relative mb-3">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#9E6752' }} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products…"
              className="pl-9"
              data-testid="ads-search-input"
            />
          </div>
          <div className="max-h-[540px] overflow-y-auto divide-y" style={{ borderColor: '#F3F4F6' }}>
            {filtered.map((p) => {
              const thumb = p.images?.[0] || p.image_url;
              const active = selected?.id === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => { setSelected(p); setImageIndex(0); setCaption(''); }}
                  data-testid={`ads-product-${p.id}`}
                  className="w-full flex items-center gap-3 px-2 py-2.5 text-left transition-colors rounded-lg"
                  style={{ background: active ? '#FFF8F0' : 'transparent', border: active ? '1px solid #FED7A5' : '1px solid transparent' }}
                >
                  {thumb ? (
                    <img src={thumb} alt="" className="w-11 h-11 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                  ) : (
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#F7F0EA' }}>
                      <ImageOff size={16} style={{ color: '#9E6752' }} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate" style={{ color: '#20212B' }}>{p.name}</p>
                    <p className="text-xs" style={{ color: '#9E6752' }}>${p.price} · {p.category}</p>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-xs text-center py-8" style={{ color: '#9E6752' }}>No products match</p>
            )}
          </div>
        </div>

        {/* Preview + export */}
        <div className="lg:col-span-3 space-y-4">
          {!selected ? (
            <div className="rounded-2xl border border-dashed p-14 text-center" style={{ borderColor: '#D4A4A8' }} data-testid="ads-empty-state">
              <Palette size={36} className="mx-auto mb-3" style={{ color: '#9E6752', opacity: 0.5 }} />
              <p className="text-sm" style={{ color: '#534145' }}>Pick a product on the left to preview its ad.</p>
            </div>
          ) : (
            <>
              {/* Controls */}
              <div className="rounded-2xl bg-white border p-4 flex flex-wrap items-center gap-4" style={{ borderColor: '#E5E0DA' }}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#9E6752' }}>Theme</span>
                  {THEMES.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setTheme(t.key)}
                      data-testid={`ads-theme-${t.key}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
                      style={{
                        background: theme === t.key ? '#534145' : '#FFF8F0',
                        color: theme === t.key ? '#FFFAF5' : '#534145',
                        border: '1px solid #53414533',
                      }}
                    >
                      <span className="w-3 h-3 rounded-full inline-block" style={{ background: t.swatch, border: `1.5px solid ${t.ring}` }} />
                      {t.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs font-semibold uppercase tracking-wider mr-0.5" style={{ color: '#9E6752' }}>Preview</span>
                  {SIZES.map((s) => (
                    <button
                      key={s.key}
                      onClick={() => setPreviewSize(s.key)}
                      data-testid={`ads-preview-size-${s.key}`}
                      className="px-2.5 py-1.5 rounded-full text-xs font-semibold transition-all"
                      style={{
                        background: previewSize === s.key ? '#9E6752' : '#FFF8F0',
                        color: previewSize === s.key ? '#FFFAF5' : '#534145',
                        border: '1px solid #9E675233',
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Photo picker — many products lead with a spec-sheet image */}
              {(selected.images || []).length > 1 && (
                <div className="rounded-2xl bg-white border p-4" style={{ borderColor: '#E5E0DA' }}>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#9E6752' }}>Featured photo</p>
                  <div className="flex gap-2 flex-wrap">
                    {selected.images.slice(0, 8).map((img, i) => (
                      <button
                        key={img}
                        onClick={() => setImageIndex(i)}
                        data-testid={`ads-photo-${i}`}
                        className="rounded-lg overflow-hidden transition-all"
                        style={{
                          width: 56,
                          height: 56,
                          border: `2px solid ${i === imageIndex ? '#9E6752' : 'transparent'}`,
                          opacity: i === imageIndex ? 1 : 0.6,
                        }}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Preview */}
              <div className="rounded-2xl bg-white border p-4 flex items-center justify-center" style={{ borderColor: '#E5E0DA', minHeight: 320 }} data-testid="ads-preview-panel">
                {previewLoading ? (
                  <div className="flex flex-col items-center gap-2 py-16">
                    <Loader2 size={26} className="animate-spin" style={{ color: '#9E6752' }} />
                    <p className="text-xs" style={{ color: '#9E6752' }}>Rendering preview…</p>
                  </div>
                ) : previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Ad preview"
                    data-testid="ads-preview-image"
                    className="rounded-xl shadow-md"
                    style={{ maxHeight: 480, maxWidth: '100%', objectFit: 'contain' }}
                  />
                ) : null}
              </div>

              {/* Export */}
              <div className="rounded-2xl bg-white border p-4" style={{ borderColor: '#E5E0DA' }}>
                <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#9E6752' }}>Export sizes</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                  {SIZES.map((s) => (
                    <label
                      key={s.key}
                      className="flex items-start gap-2 p-2.5 rounded-xl cursor-pointer transition-colors"
                      style={{
                        background: checkedSizes.has(s.key) ? '#FFF8F0' : 'white',
                        border: `1px solid ${checkedSizes.has(s.key) ? '#FED7A5' : '#E5E0DA'}`,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checkedSizes.has(s.key)}
                        onChange={() => toggleSize(s.key)}
                        data-testid={`ads-size-check-${s.key}`}
                        className="mt-0.5"
                      />
                      <span>
                        <span className="block text-xs font-semibold" style={{ color: '#20212B' }}>{s.label}</span>
                        <span className="block text-[10px]" style={{ color: '#9E6752' }}>{s.dims} · {s.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <Button
                  onClick={downloadSelected}
                  disabled={downloading || checkedSizes.size === 0}
                  data-testid="ads-download-btn"
                  className="text-white"
                  style={{ background: '#534145' }}
                >
                  {downloading ? (
                    <><Loader2 size={15} className="animate-spin mr-2" /> {downloadProgress}</>
                  ) : (
                    <><Download size={15} className="mr-2" /> Download {checkedSizes.size} size{checkedSizes.size !== 1 ? 's' : ''} ({theme})</>
                  )}
                </Button>
              </div>

              {/* AI caption */}
              <div className="rounded-2xl bg-white border p-4" style={{ borderColor: '#E5E0DA' }} data-testid="ads-caption-card">
                <p className="text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5" style={{ color: '#9E6752' }}>
                  <Sparkles size={13} /> AI Caption
                </p>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <div className="flex items-center gap-1.5">
                    {PLATFORMS.map((p) => (
                      <button
                        key={p.key}
                        onClick={() => setPlatform(p.key)}
                        data-testid={`ads-caption-platform-${p.key}`}
                        className="px-2.5 py-1.5 rounded-full text-xs font-semibold transition-all"
                        style={{
                          background: platform === p.key ? '#534145' : '#FFF8F0',
                          color: platform === p.key ? '#FFFAF5' : '#534145',
                          border: '1px solid #53414533',
                        }}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {TONES.map((t) => (
                      <button
                        key={t.key}
                        onClick={() => setTone(t.key)}
                        data-testid={`ads-caption-tone-${t.key}`}
                        className="px-2.5 py-1.5 rounded-full text-xs font-semibold transition-all"
                        style={{
                          background: tone === t.key ? '#9E6752' : '#FFF8F0',
                          color: tone === t.key ? '#FFFAF5' : '#534145',
                          border: '1px solid #9E675233',
                        }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    onClick={generateCaption}
                    disabled={captionLoading}
                    data-testid="ads-caption-generate-btn"
                    className="text-white"
                    style={{ background: '#9E6752' }}
                  >
                    {captionLoading ? (
                      <><Loader2 size={14} className="animate-spin mr-1.5" /> Writing…</>
                    ) : (
                      <><Sparkles size={14} className="mr-1.5" /> {caption ? 'Regenerate' : 'Generate caption'}</>
                    )}
                  </Button>
                </div>
                {caption && (
                  <div className="space-y-2">
                    <Textarea
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      rows={Math.min(10, Math.max(4, caption.split('\n').length + 1))}
                      className="text-sm"
                      data-testid="ads-caption-text"
                    />
                    <Button size="sm" variant="outline" onClick={copyCaption} data-testid="ads-caption-copy-btn">
                      {copied ? <><Check size={14} className="mr-1.5" style={{ color: '#4B6043' }} /> Copied</> : <><Copy size={14} className="mr-1.5" /> Copy caption</>}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      )}

      {mode === 'carousel' && <CarouselBuilder theme={theme} ThemePicker={themePickerEl} />}
      {mode === 'brand' && <BrandAdBuilder theme={theme} ThemePicker={themePickerEl} />}
    </div>
  );
}
